import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "@/lib/auth";
import { CategoriesProvider } from "@/lib/categoriesContext";
import { CartProvider } from "@/lib/cart";
import { ConfigProvider, useConfig } from "@/lib/configContext";
import { Layout } from "@/components/Layout";
import { Toaster } from "@/components/ui/sonner";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import { StaffMaintenanceBanner } from "@/components/StaffMaintenanceBanner";

import Home from "@/pages/Home";
import Category from "@/pages/Category";
import Article from "@/pages/Article";
import Plaidoyer from "@/pages/Plaidoyer";
import About from "@/pages/About";
import Search from "@/pages/Search";
import Boutique from "@/pages/Boutique";
import Product from "@/pages/Product";
import Cart from "@/pages/Cart";
import Contact from "@/pages/Contact";
import Ressources from "@/pages/Ressources";
import Login from "@/pages/Login";
import ComingSoon from "@/pages/ComingSoon";
import AdminDashboard from "@/pages/admin/Dashboard";
import ArticleEditor from "@/pages/admin/ArticleEditor";
import AdminCategories from "@/pages/admin/Categories";
import AdminProducts from "@/pages/admin/Products";
import AdminMessages from "@/pages/admin/Messages";
import AdminResources from "@/pages/admin/Resources";
import AdminProfile from "@/pages/admin/Profile";
import AdminPages from "@/pages/admin/Pages";
import PageEditor from "@/pages/admin/PageEditor";
import AdminUsers from "@/pages/admin/Users";

const PublicRoutes = () => {
  const { config, loading } = useConfig();
  const { user } = useAuth();

  // While we don't know yet, render nothing (avoids a flash of the real site
  // before flipping into maintenance mode). We also wait for the auth check
  // (`user === null`) so logged-in staff don't see ComingSoon for a frame.
  if (loading || user === null) return null;

  const isStaff = !!user && (user.role === "admin" || user.role === "editor");

  // Public visitors hit the Coming Soon wall; staff bypass it so they can
  // preview real pages while the site is officially closed.
  if (config?.maintenance && !isStaff) {
    return (
      <Routes>
        <Route path="*" element={<ComingSoon />} />
      </Routes>
    );
  }

  return (
    <>
      {config?.maintenance && isStaff && <StaffMaintenanceBanner />}
      <Layout>
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/rubrique/:slug" element={<Category />} />
          <Route path="/article/:slug" element={<Article />} />
          <Route path="/plaidoyer" element={<Plaidoyer />} />
          <Route path="/a-propos" element={<About />} />
          <Route path="/recherche" element={<Search />} />
          <Route path="/boutique" element={<Boutique />} />
          <Route path="/produit/:slug" element={<Product />} />
          <Route path="/panier" element={<Cart />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/ressources" element={<Ressources />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </Layout>
    </>
  );
};

function ProtectedAdmin({ children, adminOnly = false }) {
  const { user } = useAuth();
  if (user === null) return <div className="py-32 text-center font-mono-label">CHARGEMENT…</div>;
  // Not authenticated or not a staff role → login
  if (!user || (user.role !== "admin" && user.role !== "editor")) {
    return <Navigate to="/admin/login" replace />;
  }
  // Staff but route restricted to admins (e.g. user management, categories)
  if (adminOnly && user.role !== "admin") {
    return <Navigate to="/admin" replace />;
  }
  return children;
}

function AppRoutes() {
  return (
    <Routes>
      {/* Admin routes are ALWAYS available, regardless of maintenance mode */}
      <Route path="/admin/login" element={<Login />} />
      <Route path="/admin" element={<ProtectedAdmin><AdminDashboard /></ProtectedAdmin>} />
      <Route path="/admin/articles/nouveau" element={<ProtectedAdmin><ArticleEditor /></ProtectedAdmin>} />
      <Route path="/admin/articles/:id" element={<ProtectedAdmin><ArticleEditor /></ProtectedAdmin>} />
      <Route path="/admin/rubriques" element={<ProtectedAdmin adminOnly><AdminCategories /></ProtectedAdmin>} />
      <Route path="/admin/boutique" element={<ProtectedAdmin><AdminProducts /></ProtectedAdmin>} />
      <Route path="/admin/messages" element={<ProtectedAdmin><AdminMessages /></ProtectedAdmin>} />
      <Route path="/admin/ressources" element={<ProtectedAdmin><AdminResources /></ProtectedAdmin>} />
      <Route path="/admin/profile" element={<ProtectedAdmin><AdminProfile /></ProtectedAdmin>} />
      <Route path="/admin/pages" element={<ProtectedAdmin><AdminPages /></ProtectedAdmin>} />
      <Route path="/admin/pages/:page" element={<ProtectedAdmin><PageEditor /></ProtectedAdmin>} />
      <Route path="/admin/utilisateurs" element={<ProtectedAdmin adminOnly><AdminUsers /></ProtectedAdmin>} />
      <Route path="/*" element={<PublicRoutes />} />
    </Routes>
  );
}

function App() {
  return (
    <ErrorBoundary>
      <BrowserRouter>
        <ConfigProvider>
          <CategoriesProvider>
            <AuthProvider>
              <CartProvider>
                <AppRoutes />
                <Toaster />
              </CartProvider>
            </AuthProvider>
          </CategoriesProvider>
        </ConfigProvider>
      </BrowserRouter>
    </ErrorBoundary>
  );
}

export default App;
